import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8001';
const SESSION_TOKEN_KEY = 'admin_session_token';

const api = axios.create({
  baseURL: API_BASE_URL,
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Issue #321: Set Authorization header on api defaults for all requests
// This ensures the header is sent with every request type (GET, POST, PUT, DELETE)
const setAuthHeader = (token) => {
  if (token) {
    api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
  } else {
    delete api.defaults.headers.common['Authorization'];
  }
};

// Initialize auth header from localStorage on page load
const storedToken = localStorage.getItem(SESSION_TOKEN_KEY);
if (storedToken) {
  setAuthHeader(storedToken);
}

// Issue #388: Global 401 handler — auto-logout when admin session expires.
// App.js registers a callback here; the interceptor invokes it after
// clearing the stored token so the UI returns to the login screen and
// shows an accessible "session expired" notice instead of generic
// per-page error messages like "Fehler beim Laden der Testdaten".
let unauthorizedHandler = null;
export const setUnauthorizedHandler = (handler) => {
  unauthorizedHandler = handler;
};

api.interceptors.response.use(
  (response) => response,
  (error) => {
    const status = error.response?.status;
    const requestUrl = error.config?.url || '';
    const isLoginRequest = requestUrl.includes('/api/admin/login');
    const hadToken = !!localStorage.getItem(SESSION_TOKEN_KEY);

    if (status === 401 && !isLoginRequest && hadToken) {
      localStorage.removeItem(SESSION_TOKEN_KEY);
      setAuthHeader(null);
      if (unauthorizedHandler) {
        unauthorizedHandler();
      }
    }
    return Promise.reject(error);
  }
);

// Auth API
export const authApi = {
  login: async (username, password) => {
    const response = await api.post('/api/admin/login', { username, password });
    // Store session token if returned and set auth header
    if (response.data.session_token) {
      localStorage.setItem(SESSION_TOKEN_KEY, response.data.session_token);
      setAuthHeader(response.data.session_token);
    }
    return response;
  },
  logout: async () => {
    const response = await api.post('/api/admin/logout');
    localStorage.removeItem(SESSION_TOKEN_KEY);
    setAuthHeader(null);
    return response;
  },
  getMe: () => api.get('/api/admin/me'),
};

// Users API
export const usersApi = {
  list: (params) => api.get('/api/admin/users', { params }),
  get: (id) => api.get(`/api/admin/users/${id}`),
  update: (id, data) => api.put(`/api/admin/users/${id}`, data),
  activate: (id) => api.post(`/api/admin/users/${id}/activate`),
  deactivate: (id) => api.post(`/api/admin/users/${id}/deactivate`),
  // Issue #417: reason (>=20 chars) is now mandatory for suspend/unsuspend.
  suspend: (id, days, reason) => api.post(`/api/admin/users/${id}/suspend`, { days, reason }),
  unsuspend: (id, reason) => api.post(`/api/admin/users/${id}/unsuspend`, { reason }),
  delete: (id, reason) => api.delete(`/api/admin/users/${id}`, { data: { reason } }),
  resetPassword: (id) => api.post(`/api/admin/users/${id}/reset-password`),
  resendVerification: (id) => api.post(`/api/admin/users/${id}/resend-verification`),
  setOtp: (id, otp) => api.post(`/api/admin/users/${id}/set-otp`, { otp }),
  sendOtp: (id) => api.post(`/api/admin/users/${id}/send-otp`),
  getActivities: (id, params) => api.get(`/api/admin/users/${id}/activities`, { params }),
  getEmails: (id) => api.get(`/api/admin/users/${id}/emails`),
  // Issue #358: GDPR data export
  startDataExport: (id) => api.post(`/api/admin/users/${id}/data-export`),
  getDataExportStatus: (id, jobId) => api.get(`/api/admin/users/${id}/data-export/${jobId}/status`),
  downloadDataExport: (id, jobId) => api.get(`/api/admin/users/${id}/data-export/${jobId}/download`, {
    responseType: 'blob',
  }),
};

// Content API
export const contentApi = {
  listPosts: (params) => api.get('/api/admin/posts', { params }),
  getPost: (id) => api.get(`/api/admin/posts/${id}`),
  // Issue #408: reason (>=20 chars) is now mandatory and sent in the request body.
  deletePost: (id, reason) => api.delete(`/api/admin/posts/${id}`, { data: { reason } }),
  restorePost: (id, reason) => api.post(`/api/admin/posts/${id}/restore`, { reason }),
  listComments: (params) => api.get('/api/admin/comments', { params }),
  // Issue #410: Single comment detail (reachable for comments auto-deleted after 5+ reports).
  getComment: (id) => api.get(`/api/admin/comments/${id}`),
  // Issue #408: reason (>=20 chars) is now mandatory and sent in the request body.
  deleteComment: (id, reason) => api.delete(`/api/admin/comments/${id}`, { data: { reason } }),
  restoreComment: (id, reason) => api.post(`/api/admin/comments/${id}/restore`, { reason }),
};

// Reports API
export const reportsApi = {
  listPostReports: (status) => api.get('/api/admin/reports/posts', { params: { status } }),
  listCommentReports: (status) => api.get('/api/admin/reports/comments', { params: { status } }),
  resolvePostReport: (id, action, note) => api.post(`/api/admin/reports/posts/${id}/resolve`, { action, note }),
  resolveCommentReport: (id, action, note) => api.post(`/api/admin/reports/comments/${id}/resolve`, { action, note }),
  // Issue #410: Reports (category + optional text) for a single content item,
  // displayed in the "Meldungen" section of the post/object/comment detail page.
  listContentReportsForPost: (postId) => api.get(`/api/admin/posts/${postId}/reports`),
  listContentReportsForComment: (commentId) => api.get(`/api/admin/comments/${commentId}/reports`),
};

// Logs API
export const logsApi = {
  list: (params) => api.get('/api/admin/logs', { params }),
  // Issue #175: Backend error logs
  listErrors: (params) => api.get('/api/admin/logs/errors', { params }),
};

// Announcements API
// Issue #321: Pass empty config object to ensure interceptors run properly
export const announcementsApi = {
  list: (activeOnly) => api.get('/api/admin/announcements', { params: { active_only: activeOnly } }),
  create: (data) => api.post('/api/admin/announcements', data),
  update: (id, data) => api.put(`/api/admin/announcements/${id}`, data, {}),
  delete: (id) => api.delete(`/api/admin/announcements/${id}`, {}),
};

// Stats API
export const statsApi = {
  getOverview: () => api.get('/api/admin/stats/overview'),
  getUsers: () => api.get('/api/admin/stats/users'),
  getContent: () => api.get('/api/admin/stats/content'),
  // Issue #291: User engagement KPIs
  getUserEngagement: (startDate, endDate) => api.get('/api/admin/stats/user-engagement', {
    params: { start_date: startDate, end_date: endDate }
  }),
  exportUserEngagement: (kpis, startDate, endDate) => api.get('/api/admin/stats/user-engagement/export', {
    params: { kpis: kpis.join(','), start_date: startDate, end_date: endDate },
    responseType: 'blob'
  }),
  // Issue #313: Ad revenue KPIs
  getRevenue: (startDate, endDate, universities, adFormats) => api.get('/api/admin/stats/revenue', {
    params: {
      start_date: startDate,
      end_date: endDate,
      universities: universities ? universities.join(',') : undefined,
      ad_formats: adFormats ? adFormats.join(',') : undefined
    }
  }),
  exportRevenue: (startDate, endDate, universities, adFormats, kpis) => api.get('/api/admin/stats/revenue/export', {
    params: {
      start_date: startDate,
      end_date: endDate,
      universities: universities ? universities.join(',') : undefined,
      ad_formats: adFormats ? adFormats.join(',') : undefined,
      kpis: kpis ? kpis.join(',') : undefined
    },
    responseType: 'blob'
  }),
};

// Courses API (Issue #180)
export const coursesApi = {
  list: (params) => api.get('/api/admin/courses', { params }),
  get: (id) => api.get(`/api/admin/courses/${id}`),
  create: (data) => api.post('/api/admin/courses', data),
  update: (id, data) => api.put(`/api/admin/courses/${id}`, data),
  delete: (id) => api.delete(`/api/admin/courses/${id}`, {}),
  getUniversities: () => api.get('/api/admin/courses/universities'),
  getStudyPrograms: (university) => api.get('/api/admin/courses/study-programs', { params: { university } }),
  // Issue #181: Import courses from CSV
  importCourses: (courses) => api.post('/api/admin/courses/import', { courses }),
};

// Feedback API (Issue #245)
export const feedbackApi = {
  list: (params) => api.get('/api/admin/feedback', { params }),
  getUniversities: () => api.get('/api/admin/feedback/universities'),
};

// Backups API (Issue #338)
export const backupsApi = {
  getTables: () => api.get('/api/admin/backups/tables'),
  create: (data) => api.post('/api/admin/backups/create', data, {
    responseType: data.destination === 'download' ? 'blob' : 'json',
  }),
  list: () => api.get('/api/admin/backups/list'),
  download: (filename) => api.get(`/api/admin/backups/download/${filename}`, {
    responseType: 'blob',
  }),
  delete: (filename) => api.delete(`/api/admin/backups/${filename}`, {}),
  inspect: (filename) => api.post('/api/admin/backups/inspect', { filename }),
  inspectUpload: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/api/admin/backups/inspect-upload', file, {
      headers: { 'Content-Type': 'application/octet-stream' },
    });
  },
  restore: (data) => api.post('/api/admin/backups/restore', data),
  restoreUpload: (file) => api.post('/api/admin/backups/restore-upload', file, {
    headers: { 'Content-Type': 'application/octet-stream' },
  }),
};

// Beta Test Data API (Issue #359)
export const betaApi = {
  // List scheduled test data
  listScheduled: (params) => api.get('/api/admin/beta/scheduled', { params }),
  // List uploaded test data
  listUploaded: (params) => api.get('/api/admin/beta/uploaded', { params }),
  // Get single test data item
  get: (id) => api.get(`/api/admin/beta/test-data/${id}`),
  // Create new scheduled test data
  create: (data) => api.post('/api/admin/beta/test-data', data),
  // Update scheduled test data
  update: (id, data) => api.put(`/api/admin/beta/test-data/${id}`, data),
  // Delete test data
  delete: (id) => api.delete(`/api/admin/beta/test-data/${id}`, {}),
  // Get available universities
  getUniversities: () => api.get('/api/admin/beta/universities'),
  // Get posts for a university (for comment parent selection)
  getPosts: (university, limit) => api.get(`/api/admin/beta/posts/${university}`, { params: { limit } }),
  // Get courses for a university (for course review selection)
  getCourses: (university, limit) => api.get(`/api/admin/beta/courses/${university}`, { params: { limit } }),
  // Issue #366: Beta star-rating results (count, average, rows)
  listRatings: () => api.get('/api/admin/beta/ratings'),
};

// Settings API (website password gate)
export const settingsApi = {
  getWebsitePasswordSet: () => api.get('/api/admin/settings/website-password-set'),
  setWebsitePassword: (password) => api.put('/api/admin/settings/website-password', { password }),
  deleteWebsitePassword: () => api.delete('/api/admin/settings/website-password', {}),
};

export default api;
